import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Box, VStack, Text, useToast } from '@chakra-ui/react';
import API from '../api/axiosConfig';
import ErrorAlert from '../components/ErrorAlert';
import "../styles/pages/VerifyOTP.css";

const OTP_EXPIRATION_TIME = 300;

const VerifyOTP = () => {
    const [otp, setOtp] = useState('');
    const [token, setToken] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [countdown, setCountdown] = useState(OTP_EXPIRATION_TIME);
    const navigate = useNavigate();
    const toast = useToast();

    useEffect(() => {
        const otpToken = sessionStorage.getItem('otpToken');
        if (otpToken) {
            setToken(otpToken);
        } else {
            toast({
                title: "Session Error",
                description: "Session expired. Please try logging in again.",
                status: "error",
                duration: 9000,
                isClosable: true,
            });
            navigate('/login');
        }
    }, [navigate, toast]);

    useEffect(() => {
        if (countdown > 0) {
            const timerId = setInterval(() => {
                setCountdown(prevCountdown => prevCountdown - 1);
            }, 1000);
            return () => clearInterval(timerId);
        } else {
            toast({
                title: "OTP Expired",
                description: "Your SMS Passcode has expired. Please login again to request a new one.",
                status: "error",
                duration: 9000,
                isClosable: true,
            });
        }
    }, [countdown, toast]);

    const handleInputChange = (event) => {
        const value = event.target.value;
        // Only allow numeric input
        if (/^\d*$/.test(value) && value.length <= 6) {
            setOtp(value);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (otp.length !== 6) {
            toast({
                title: "Validation Error",
                description: "OTP must be 6 digits.",
                status: "error",
                duration: 9000,
                isClosable: true,
            });
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            const response = await API.post('/web/otp/verify', { otp, token }, {
                headers: { 'Authorization': `Bearer ${sessionStorage.getItem('tempToken')}` }
            });
            if (response.data.message === 'Authentication successful') {
                toast({
                    title: "Success",
                    description: "Authentication successful!",
                    status: "success",
                    duration: 5000,
                    isClosable: true,
                });
              
                navigate('/dashboard');
            } else {
                throw new Error(response.data.message || "Failed to verify OTP.");
            }
        } catch (error) {
            setError(error.message || "An error occurred while verifying the OTP.");
        } finally {
            setIsLoading(false);
        }
    };

    const formatCountdown = () => {
        const minutes = Math.floor(countdown / 60);
        const seconds = countdown % 60;
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };

    const handleBackToLogin = () => {
        const uuid = localStorage.getItem('UUID');
        console.log("UUID from localStorage:", uuid);

        document.cookie = "authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
        sessionStorage.clear();

        if (uuid) {
            window.location.href = `/${uuid}`;
        } else {
            navigate('/login');
        }
    };

    return (
        <Box className="verify-otp-wrapper">
            <VStack className="verify-otp-container">
                {error && <ErrorAlert title="Verification Error" description={error} onClose={() => setError('')} />}
                
                <Box className="otp-card">
                    <div className="icon-container">📱</div>
                    
                    <h1 className="otp-title">Verify Your Code</h1>
                    <Text className="otp-subtitle">
                        A 6-digit passcode has been sent via SMS to your mobile phone. Please enter this code here.
                    </Text>

                    <div className="timer-container">
                        <div className="timer">{formatCountdown()}</div>
                        <div className="timer-label">Time Remaining</div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <VStack spacing={6}>
                            <Input
                                type="text"
                                inputMode="numeric"
                                pattern="\d*"
                                value={otp}
                                onChange={handleInputChange}
                                placeholder="Enter 6-Digit Code"
                                isRequired
                                focusBorderColor="blue.500"
                                isDisabled={isLoading}
                                className="otp-input"
                                maxLength={6}
                                textAlign="center"
                                fontSize="2xl"
                                letterSpacing="0.5em"
                            />
                            <Button
                                type="submit"
                                isLoading={isLoading}
                                loadingText="Verifying"
                                className="submit-btn"
                                width="100%"
                            >
                                Verify & Continue
                            </Button>
                        </VStack>
                    </form>

                    <div className="resend-container">
                        <p className="resend-text">Didn't receive the code?</p>
                        <button className="resend-btn" type="button">Resend Code</button>
                    </div>
                </Box>

                <Text
                    as="a"
                    onClick={handleBackToLogin}
                    cursor="pointer"
                    mt={4}
                    className="back-link"
                >
                    ← Back to Login
                </Text>
            </VStack>
        </Box>
    );
};

export default VerifyOTP;